/**
 * Testing Mode for 5-Layer Scan Accuracy System
 * 
 * Enables enhanced debugging, bypassing validations, and test utilities
 * for development and testing of the accuracy layers.
 * 
 * Enable testing mode by setting REACT_APP_TESTING_MODE=true in .env.local
 * Or by calling enableTestingMode() in browser console
 */

import type { ScanAccuracyResult } from './scanAccuracyLayers';
import type { PhotoMetadata } from './photoProcessing';
import type { FaceMetadata } from '../services/rekognitionService';
import type { RegionAcneSummary } from '../services/scanService';

export interface TestingModeConfig {
  enabled: boolean;
  bypassLayer1: boolean; // Skip image quality validation
  bypassLayer2: boolean; // Skip normalization checks
  bypassLayer5: boolean; // Skip stabilization (use raw results)
  verboseLogging: boolean; // Enhanced console logging
  showLayerDetails: boolean; // Display detailed layer results
  mockRekognition: boolean; // Use mock face metadata
  allowLowQuality: boolean; // Allow low-quality images to pass Layer 1
}

let testingConfig: TestingModeConfig = {
  enabled: false,
  bypassLayer1: false,
  bypassLayer2: false,
  bypassLayer5: false,
  verboseLogging: false,
  showLayerDetails: false,
  mockRekognition: false,
  allowLowQuality: false,
};

/**
 * Check if testing mode is enabled via environment variable, URL parameter, or localStorage
 */
function checkEnvironmentTestingMode(): boolean {
  // Check URL parameter first (enables testing mode via ?testing=true)
  if (typeof window !== 'undefined' && window.location) {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('testing') === 'true' || urlParams.get('testing_mode') === 'true') {
      // Enable testing mode and persist to localStorage
      if (window.localStorage) {
        try {
          localStorage.setItem('glowlens_testing_mode', 'true');
        } catch (e) {
          // Ignore errors
        }
      }
      return true;
    }
  }
  
  // Check environment variable
  if (typeof process !== 'undefined' && process.env) {
    return process.env.REACT_APP_TESTING_MODE === 'true' ||
           process.env.REACT_APP_TESTING_MODE === '1' ||
           process.env.NODE_ENV === 'test';
  }
  
  // Check localStorage for browser-based testing
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      return localStorage.getItem('glowlens_testing_mode') === 'true';
    } catch (e) {
      return false;
    }
  }
  
  return false;
}

/**
 * Initialize testing mode from environment, URL parameter, or localStorage
 */
export function initializeTestingMode(): void {
  const isEnabled = checkEnvironmentTestingMode();
  if (isEnabled) {
    testingConfig.enabled = true;
    testingConfig.verboseLogging = true;
    testingConfig.showLayerDetails = true;
    
    // Check if enabled via URL parameter
    if (typeof window !== 'undefined' && window.location) {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('testing') === 'true' || urlParams.get('testing_mode') === 'true') {
        console.log('[TESTING MODE] Enabled via URL parameter');
        // Persist to localStorage
        if (window.localStorage) {
          try {
            localStorage.setItem('glowlens_testing_mode', 'true');
          } catch (e) {
            // Ignore errors
          }
        }
        return;
      }
    }
    
    console.log('[TESTING MODE] Enabled via environment/localStorage');
  }
}

/**
 * Enable testing mode programmatically
 */
export function enableTestingMode(config?: Partial<TestingModeConfig>): void {
  testingConfig = {
    ...testingConfig,
    enabled: true,
    verboseLogging: true,
    showLayerDetails: true,
    ...config,
  };
  
  // Persist to localStorage
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      localStorage.setItem('glowlens_testing_mode', 'true');
      localStorage.setItem('glowlens_testing_config', JSON.stringify(testingConfig));
    } catch (e) {
      console.warn('[TESTING MODE] Could not save to localStorage:', e);
    }
  }
  
  console.log('[TESTING MODE] Enabled with config:', testingConfig);
}

/**
 * Disable testing mode
 */
export function disableTestingMode(): void {
  testingConfig.enabled = false;
  
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      localStorage.removeItem('glowlens_testing_mode');
      localStorage.removeItem('glowlens_testing_config');
    } catch (e) {
      // Ignore errors
    }
  }
  
  console.log('[TESTING MODE] Disabled');
}

/**
 * Get current testing mode configuration
 */
export function getTestingConfig(): TestingModeConfig {
  // Try to load from localStorage if not initialized
  if (typeof window !== 'undefined' && window.localStorage && !testingConfig.enabled) {
    try {
      const saved = localStorage.getItem('glowlens_testing_config');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.enabled) {
          testingConfig = { ...testingConfig, ...parsed };
        }
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
  
  return { ...testingConfig };
}

/**
 * Check if testing mode is enabled
 */
export function isTestingModeEnabled(): boolean {
  return testingConfig.enabled || checkEnvironmentTestingMode();
}

/**
 * Create mock face metadata for testing
 */
export function createMockFaceMetadata(
  overrides?: Partial<FaceMetadata>
): FaceMetadata {
  return {
    confidence: overrides?.confidence ?? 95,
    boundingBox: overrides?.boundingBox ?? {
      width: 0.4,
      height: 0.5,
      left: 0.3,
      top: 0.2,
    },
    quality: overrides?.quality ?? {
      brightness: 100,
      sharpness: 85,
    },
    ageRange: overrides?.ageRange ?? {
      low: 20,
      high: 30,
    },
    gender: overrides?.gender ?? {
      value: 'Female',
      confidence: 95,
    },
    emotions: overrides?.emotions ?? [
      { type: 'HAPPY', confidence: 80 },
    ],
    ...overrides,
  };
}

/**
 * Create mock photo metadata for testing
 */
export function createMockPhotoMetadata(
  overrides?: Partial<PhotoMetadata>
): PhotoMetadata {
  return {
    width: overrides?.width ?? 1920,
    height: overrides?.height ?? 2560,
    sizeKB: overrides?.sizeKB ?? 500,
    mime: overrides?.mime ?? 'image/jpeg',
    hash: overrides?.hash ?? 'mock_hash_' + Math.random().toString(36).substring(7),
    brightness: overrides?.brightness ?? 100,
    facesDetected: overrides?.facesDetected ?? 1,
    ...overrides,
  };
}

/**
 * Log detailed layer information in testing mode
 */
export function logLayerDetails(result: ScanAccuracyResult): void {
  if (!testingConfig.showLayerDetails) return;
  
  console.group('[TESTING MODE] 5-Layer Accuracy Details');
  
  console.log('📊 Overall Results', {
    tier: result.tier,
    finalGlowScore: result.finalGlowScore,
    overallConfidence: result.overallConfidence,
    overallQualityScore: result.overallQualityScore,
    scanQualityScore: result.scanQualityScore,
  });
  
  console.log('🔍 Layer 1: Image Capture Quality', {
    valid: result.layer1.valid,
    qualityScore: result.layer1.qualityScore,
    errors: result.layer1.errors,
    warnings: result.layer1.warnings,
    checks: result.layer1.checks,
  });
  
  console.log('🎨 Layer 2: Image Normalization', {
    normalized: result.layer2.normalized,
    faceAligned: result.layer2.faceAligned,
    alignmentScore: result.layer2.alignmentScore,
    metrics: result.layer2.normalizationMetrics,
  });
  
  console.log('🔬 Layer 3: Acne Detection', {
    spotsDetected: result.layer3.spots.length,
    confidenceThreshold: result.layer3.confidenceThreshold,
    filteredCount: result.layer3.filteredCount,
    byType: result.acneCountByType,
  });
  
  console.log('🏷️ Layer 4: Label Quality', {
    isConservative: result.layer4.isConservative,
    skinToneVariance: result.layer4.skinToneVariance,
    lightingVariance: result.layer4.lightingVariance,
    labelQuality: result.layer4.labelQuality,
    notes: result.layer4.notes,
  });
  
  console.log('📈 Layer 5: Stabilization', {
    smoothed: result.layer5.smoothed,
    stabilizationApplied: result.layer5.stabilizationApplied,
    overallTrend: result.layer5.overallTrend,
    dayToDayChange: result.layer5.dayToDayChange.toFixed(2) + '%',
    zoneTrends: result.layer5.zoneTrends,
  });
  
  console.log('📍 Zone Breakdown', result.zoneBreakdown);
  
  console.groupEnd();
}

/**
 * Test individual layer functions (for unit testing)
 */
export function testLayer1Validation(
  frontMeta: PhotoMetadata,
  frontFace?: FaceMetadata,
  leftSideMeta?: PhotoMetadata,
  leftSideFace?: FaceMetadata,
  rightSideMeta?: PhotoMetadata,
  rightSideFace?: FaceMetadata
) {
  const { validateImageCaptureQuality } = require('./scanAccuracyLayers');
  return validateImageCaptureQuality(
    frontMeta,
    leftSideMeta,
    rightSideMeta,
    frontFace,
    leftSideFace,
    rightSideFace
  );
}

/**
 * Test layer 5 stabilization with mock previous scan
 */
export function testLayer5Stabilization(
  currentRegions: RegionAcneSummary[],
  previousGlowScore: number,
  tier: 'plus' | 'pro' | 'elite' = 'plus'
) {
  const { stabilizeResults } = require('./scanAccuracyLayers');
  const mockPreviousScan = {
    glowScore: previousGlowScore,
    analysis: {
      acneAnalysis: {
        regions: currentRegions.map(r => ({
          ...r,
          activeLesionCount: Math.max(0, r.activeLesionCount - 2), // Simulate improvement
        })),
      },
    },
  } as any;
  
  return stabilizeResults(currentRegions, mockPreviousScan, tier);
}

/**
 * Expose testing utilities to window for browser console access
 */
if (typeof window !== 'undefined') {
  (window as any).glowlensTesting = {
    enable: enableTestingMode,
    disable: disableTestingMode,
    getConfig: getTestingConfig,
    isEnabled: isTestingModeEnabled,
    createMockFace: createMockFaceMetadata,
    createMockPhoto: createMockPhotoMetadata,
    testLayer1: testLayer1Validation,
    testLayer5: testLayer5Stabilization,
    logLayerDetails,
  };
  
  // Initialize on load
  initializeTestingMode();
}

